import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, Trash2, Upload, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Eyebrow } from "@/components/site/Reveal";
import { BlueprintGrid } from "@/components/site/Blueprint";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "carvings-staff-token";
const detail = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg).join(" ") : "Something went wrong");
const authFetch = (path, opts = {}) => fetch(`${API}${path}`, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
const input = "mt-2 w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-900";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(detail(data.detail));
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="max-w-md bg-white border border-stone-200 p-8 lg:p-10" data-testid="staff-login-form">
      <span className="inline-flex w-10 h-10 items-center justify-center bg-stone-900 text-[#FBFBF9]"><Lock size={16} /></span>
      <h1 className="font-serif text-3xl text-stone-900 mt-6">Staff sign in</h1>
      <p className="mt-2 text-sm text-stone-600 font-light">For the Carvings team only. Upload designs, site photos and short videos to the website.</p>
      <label className="field block relative mt-8"><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Email</span><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="staff-email-input" /><span className="field-line" /></label>
      <label className="field block relative mt-6"><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Password</span><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={input} data-testid="staff-password-input" /><span className="field-line" /></label>
      <motion.button whileTap={{ scale: 0.97 }} disabled={busy} className="btn-dark mt-8 disabled:opacity-60" data-testid="staff-login-btn">{busy ? <span className="blocks"><i /><i /><i /></span> : "Sign in"}</motion.button>
    </form>
  );
};

const Uploader = ({ cats, onDone }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState(cats[0] || "Design");
  const [progress, setProgress] = useState(null);
  const ref = useRef(null);
  const pick = (f) => {
    if (!f) return;
    const isVideo = f.type.startsWith("video/");
    const max = isVideo ? 20 : 10;
    if (f.size > max * 1024 * 1024) return toast.error(`${isVideo ? "Videos" : "Images"} must be under ${max} MB`);
    setFile(f); if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };
  const submit = (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a photo or video first");
    const fd = new FormData(); fd.append("file", file); fd.append("title", title); fd.append("caption", caption); fd.append("category", category);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/api/media`);
    xhr.setRequestHeader("Authorization", `Bearer ${localStorage.getItem(TOKEN_KEY)}`);
    xhr.upload.onprogress = (ev) => ev.lengthComputable && setProgress(Math.round((ev.loaded / ev.total) * 100));
    xhr.onload = () => {
      setProgress(null);
      if (xhr.status < 300) { toast.success("Uploaded — it is live on the website"); setFile(null); setTitle(""); setCaption(""); if (ref.current) ref.current.value = ""; onDone(JSON.parse(xhr.responseText)); }
      else { try { toast.error(detail(JSON.parse(xhr.responseText).detail)); } catch { toast.error("Upload failed"); } }
    };
    xhr.onerror = () => { setProgress(null); toast.error("Upload failed — check your connection"); };
    setProgress(0); xhr.send(fd);
  };
  return (
    <form onSubmit={submit} className="bg-white border border-stone-200 p-6 lg:p-8" data-testid="staff-upload-form">
      <h2 className="font-serif text-2xl text-stone-900">Upload to the website</h2>
      <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}
        className={`mt-6 flex flex-col items-center justify-center gap-3 border border-dashed p-8 text-center cursor-pointer transition-colors ${file ? "border-[#2D6A4F] bg-[#2D6A4F]/5" : "border-stone-300 hover:border-stone-900"}`} data-testid="staff-dropzone">
        <Upload size={20} className="text-stone-500" />
        <span className="text-sm text-stone-700">{file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(1)} MB` : "Drop a photo or video here, or tap to choose"}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">JPG · PNG · WebP up to 10 MB · MP4 / MOV / WebM up to 20 MB</span>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="staff-file-input" />
      </label>
      <div className="grid sm:grid-cols-2 gap-6 mt-6">
        <label className="field block relative"><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Title</span><input value={title} onChange={(e) => setTitle(e.target.value)} className={input} placeholder="Ajeer Residence — front elevation" data-testid="staff-title-input" /><span className="field-line" /></label>
        <label className="field block relative"><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={input} data-testid="staff-category-select">{cats.map((c) => <option key={c}>{c}</option>)}</select><span className="field-line" /></label>
      </div>
      <label className="field block relative mt-6"><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">Caption (optional)</span><input value={caption} onChange={(e) => setCaption(e.target.value)} className={input} placeholder="Laterite walls going up, week 6" data-testid="staff-caption-input" /><span className="field-line" /></label>
      <div className="mt-8 flex items-center gap-5">
        <motion.button whileTap={{ scale: 0.97 }} disabled={progress !== null} className="btn-dark disabled:opacity-60" data-testid="staff-upload-btn">{progress !== null ? `Uploading ${progress}%` : "Publish to website"}</motion.button>
        {progress !== null && <span className="h-px flex-1 bg-stone-200 overflow-hidden"><motion.span className="block h-full bg-[#B85C38]" animate={{ width: `${progress}%` }} /></span>}
      </div>
    </form>
  );
};

const StaffAdmin = () => {
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const load = () => authFetch("/api/auth/staff").then((r) => r.json()).then((d) => Array.isArray(d) && setStaff(d));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    const res = await authFetch("/api/auth/staff", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
    const data = await res.json();
    if (!res.ok) return toast.error(detail(data.detail));
    toast.success(`${data.name} can now sign in`); setF({ name: "", email: "", password: "" }); load();
  };
  return (
    <div className="bg-white border border-stone-200 p-6 lg:p-8" data-testid="staff-admin-panel">
      <h2 className="font-serif text-2xl text-stone-900 flex items-center gap-3"><UserPlus size={18} /> Team accounts</h2>
      <ul className="mt-4 divide-y divide-stone-200 text-sm" data-testid="staff-list">{staff.map((s) => <li key={s.email} className="py-2 flex justify-between gap-3"><span>{s.name} <span className="text-stone-400">· {s.email}</span></span><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{s.role}</span></li>)}</ul>
      <form onSubmit={add} className="mt-6 grid sm:grid-cols-3 gap-4">
        <input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} data-testid="new-staff-name" />
        <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} data-testid="new-staff-email" />
        <input required type="password" minLength={8} placeholder="Password (8+)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={input} data-testid="new-staff-password" />
        <button className="btn-ghost sm:col-span-3 justify-self-start" data-testid="new-staff-btn">Add staff member</button>
      </form>
    </div>
  );
};

export default function StaffPage() {
  const [user, setUser] = useState(undefined);
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const loadItems = () => fetch(`${API}/api/media?limit=200`).then((r) => r.json()).then(setItems).catch(() => {});
  useEffect(() => {
    fetch(`${API}/api/media/categories`).then((r) => r.json()).then(setCats).catch(() => {});
    if (!localStorage.getItem(TOKEN_KEY)) return setUser(null);
    authFetch("/api/auth/me").then(async (r) => setUser(r.ok ? await r.json() : null)).catch(() => setUser(null));
  }, []);
  useEffect(() => { if (user) loadItems(); }, [user]);
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setUser(null); };
  const remove = async (m) => {
    if (!window.confirm(`Remove "${m.title}" from the website?`)) return;
    const res = await authFetch(`/api/media/${m.id}`, { method: "DELETE" });
    if (!res.ok) return toast.error(detail((await res.json()).detail));
    toast.success("Removed"); loadItems();
  };

  return (
    <section className="relative bg-[#F4F3EE] min-h-[80vh] py-20 lg:py-28 overflow-hidden" data-testid="staff-page">
      <BlueprintGrid className="opacity-60" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div><Eyebrow className="mb-4">Staff studio</Eyebrow><h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-stone-900">{user ? `Hello, ${user.name.split(" ")[0]}.` : "Private area"}</h1></div>
          {user && <button onClick={logout} className="btn-ghost" data-testid="staff-logout-btn"><LogOut size={14} /> Sign out</button>}
        </div>
        {user === undefined ? <p className="font-mono text-xs uppercase tracking-[0.2em] text-stone-500">Checking session…</p> : !user ? <Login onLogin={setUser} /> : (
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 space-y-8">
              <Uploader cats={cats} onDone={loadItems} />
              {user.role === "admin" && <StaffAdmin />}
            </div>
            <div className="lg:col-span-5 bg-white border border-stone-200 p-6" data-testid="staff-media-list">
              <h2 className="font-serif text-2xl text-stone-900">Live on the website <span className="font-mono text-xs text-stone-400">({items.length})</span></h2>
              <ul className="mt-4 divide-y divide-stone-200 max-h-[70vh] overflow-y-auto">
                {items.map((m) => (
                  <li key={m.id} className="py-3 flex items-center gap-4" data-testid={`staff-media-${m.id}`}>
                    {m.kind === "video" ? <video src={`${API}${m.url}#t=0.1`} muted className="w-16 h-12 object-cover bg-stone-200" /> : <img src={`${API}${m.url}`} alt="" className="w-16 h-12 object-cover bg-stone-200" />}
                    <div className="flex-1 min-w-0"><p className="text-sm text-stone-900 truncate">{m.title}</p><p className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500">{m.category} · {m.uploaded_by}</p></div>
                    <button onClick={() => remove(m)} className="w-9 h-9 flex items-center justify-center text-stone-400 hover:text-[#B85C38]" aria-label="Remove" data-testid={`staff-media-delete-${m.id}`}><Trash2 size={15} /></button>
                  </li>
                ))}
                {items.length === 0 && <li className="py-6 text-sm text-stone-500">Nothing uploaded yet.</li>}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
